import type { OrderPaymentSource } from '../constants/bank'
import { z } from 'zod'
import { POSITIVE_PRICE, SAFE_STRING, SellerEntitySchema } from './request.types'
import { MAX_ORDER_MONEY_RUB } from '../constants/orders'
import type { VatMode } from '../constants/vat'
import type {
  OrderStatus,
  OrderPaymentTerms,
  OrderInvoiceStatus,
  OrderPaymentStatus,
  OrderDocumentKind,
  OrderInvoiceColumn,
} from '../constants/orders'

// ─────────────────────────────────────────────
//  Zod-схемы запросов API «Заказ клиента» (Блок 2)
// ─────────────────────────────────────────────

export const OrderPaymentTermsSchema = z.enum([
  'PREPAYMENT',
  'GUARANTEE_LETTER',
  'DEFERRED',
  'POSTPAYMENT',
])

export const OrderPaymentStatusSchema = z.enum(['PLANNED', 'RECEIVED', 'CANCELLED'])

export const OrderStatusSchema = z.enum([
  'NEW',
  'PENDING_ROP',
  'CONFIRMED',
  'AWAITING_PAYMENT',
  'PARTIALLY_PAID',
  'PAID',
  'CLOSED',
  'CANCELLED',
  'ARCHIVED',
])

export const OrderDocumentKindSchema = z.enum([
  'INVOICE_TEMPLATE',
  'GUARANTEE_LETTER',
  'PAYMENT_PROOF',
  'INVOICE_PDF',
  'OTHER',
])

// Юрлицо-продавец счёта (Р1) — схема общая с заявкой, объявлена в request.types.
export { SellerEntitySchema }

// Создание счёта-шаблона (и, при первом вызове по заявке, самого заказа).
// requestItemIds — какие ORDERED-позиции заявки включить в счёт; пусто = все.
// paymentTermsType / prepaymentPercent применяются ТОЛЬКО при создании заказа
// (первый счёт); у уже существующего заказа условие оплаты не меняется этим
// эндпоинтом.
export const CreateInvoiceFromRequestSchema = z.object({
  requestItemIds: z.array(z.string().uuid()).max(2000).optional(),
  paymentTermsType: OrderPaymentTermsSchema,
  // Плановая предоплата % (для условия «Предоплата»; для отсрочки/постоплаты = 0).
  prepaymentPercent: z.number().finite().min(0).max(100),
  // Дни отсрочки/постоплаты (для «Отсрочка по договору»/«Постоплата») — когда ждать деньги.
  deferralDays: z.number().int().min(0).max(3650).nullable().optional(),
  vatRate: z.number().finite().min(0).max(100).default(0),
  entityId: SellerEntitySchema.nullable().optional(),
  invoiceNumber: SAFE_STRING(40).nullable().optional(),
})
export type CreateInvoiceFromRequestDto = z.infer<typeof CreateInvoiceFromRequestSchema>

// Смена условий оплаты в уже оформленном заказе (12.10.2026): заказ создаётся
// отметкой «Счёт» с предоплатой 100 %, реальные условия менеджер ставит здесь.
export const UpdatePaymentTermsSchema = z.object({
  paymentTermsType: OrderPaymentTermsSchema,
  prepaymentPercent: z.number().finite().min(0).max(100),
  deferralDays: z.number().int().min(0).max(3650).nullable().optional(),
})
export type UpdatePaymentTermsDto = z.infer<typeof UpdatePaymentTermsSchema>

// Массовое назначение «сопровода» (кто закупает) выбранным позициям заказа.
export const AssignHandlerSchema = z.object({
  requestItemIds: z.array(z.string().uuid()).min(1).max(2000),
  handlerId: z.string().uuid().nullable(),
})
export type AssignHandlerDto = z.infer<typeof AssignHandlerSchema>

// Правка зафиксированного платежа.
export const UpdatePaymentSchema = z.object({
  amount: z.number().finite().positive().max(1_000_000_000).optional(),
  kind: SAFE_STRING(20).nullable().optional(),
  paidAt: z.string().datetime().nullable().optional(),
  note: SAFE_STRING(2000).nullable().optional(),
})
export type UpdatePaymentDto = z.infer<typeof UpdatePaymentSchema>

// Согласование заказа руководителем (РОП) или переопределение админом.
export const ApproveOrderSchema = z.object({
  approve: z.boolean(),
  note: SAFE_STRING(2000).nullable().optional(),
})
export type ApproveOrderDto = z.infer<typeof ApproveOrderSchema>

// Ручная фиксация оплаты (частичные оплаты поддержаны). Сумма строго > 0.
export const CreatePaymentSchema = z.object({
  invoiceId: z.string().uuid().nullable().optional(),
  amount: z.number().finite().positive().max(1_000_000_000),
  currency: SAFE_STRING(3).optional(),
  kind: SAFE_STRING(20).nullable().optional(),
  paidAt: z.string().datetime().nullable().optional(),
  note: SAFE_STRING(2000).nullable().optional(),
  // «Внести, хотя такая оплата уже пришла из банка» — защита от задвоения (Б3).
  confirmDuplicate: z.boolean().optional(),
})
export type CreatePaymentDto = z.infer<typeof CreatePaymentSchema>

// Ручная смена статуса заказа (закрытие / отказ / архив). Для CANCELLED причина
// обязательна — проверяется в сервисе с русским сообщением.
export const UpdateOrderStatusSchema = z.object({
  status: OrderStatusSchema,
  note: SAFE_STRING(2000).nullable().optional(),
})
export type UpdateOrderStatusDto = z.infer<typeof UpdateOrderStatusSchema>

// Правка метаданных счёта: номер из УНФ, статус. Используется когда бухгалтер
// проставил реальный номер счёта 1С:УНФ.
export const UpdateInvoiceSchema = z.object({
  invoiceNumber: SAFE_STRING(40).nullable().optional(),
  status: z.enum(['DRAFT', 'ISSUED', 'PAID', 'CANCELLED']).optional(),
})
export type UpdateInvoiceDto = z.infer<typeof UpdateInvoiceSchema>

// Правка позиции счёта (В2: счёт правится по позициям). Можно менять цену за
// единицу (₽) и/или количество. Бэк пересчитывает sum = price×quantity, сумму
// счёта (= Σ позиций) и статус оплаты заказа; счёт помечается amountSource='manual'.
export const UpdateInvoiceItemSchema = z
  .object({
    price: POSITIVE_PRICE.optional(),
    quantity: z.number().finite().positive().max(10_000_000).optional(),
    /** Ручное «Наименование» для УНФ; null — сброс к авто-вычислению. */
    unfName: SAFE_STRING(300).nullable().optional(),
    /** Бренд — синкается и в снапшот счёта, и в живую позицию заявки. */
    brand: SAFE_STRING(100).nullable().optional(),
    /** Сегмент партномера — пишется в позицию заявки (request_items.category). */
    category: SAFE_STRING(100).nullable().optional(),
  })
  .refine(
    (d) =>
      d.price !== undefined ||
      d.quantity !== undefined ||
      d.unfName !== undefined ||
      d.brand !== undefined ||
      d.category !== undefined,
    { message: 'Укажите, что изменить в позиции счёта' },
  )
  // S500-10: цена и количество по отдельности валидны, а сумма строки лежит в
  // Decimal(14,2). Из интерфейса приходит ОДНО поле (второе берётся из БД), так
  // что основная защита — в сервисе (orders.money.ts / updateInvoiceItem); здесь
  // отсекается только запрос, где оба поля пришли вместе.
  .refine(
    (d) =>
      d.price === undefined ||
      d.quantity === undefined ||
      d.price * d.quantity <= MAX_ORDER_MONEY_RUB,
    {
      message:
        'Сумма позиции (цена × количество) больше допустимой — не более 999 999 999 999,99 ₽. Проверьте цену и количество',
    },
  )
export type UpdateInvoiceItemDto = z.infer<typeof UpdateInvoiceItemSchema>

export const ListOrdersQuerySchema = z.object({
  status: OrderStatusSchema.optional(),
  managerId: z.string().uuid().optional(),
  clientId: z.string().uuid().optional(),
  search: SAFE_STRING(200).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  // Паритет с cursorQuery в api (core/validation/pagination): shared не может
  // импортировать хелпер из api, поэтому граница продублирована.
  cursor: z.string().max(500).optional(),
  // 1000 — паритет с «Очередью квот»: страница грузит до 1000 заказов,
  // дальше автоподгрузка при прокрутке (просьба заказчика 02.09).
  limit: z.coerce.number().int().min(1).max(1000).default(50),
})
export type ListOrdersQueryDto = z.infer<typeof ListOrdersQuerySchema>

// Переэкспорт POSITIVE_PRICE для api-схем модуля заказов при необходимости.
export { POSITIVE_PRICE }

// ─────────────────────────────────────────────
//  DTO ответов (чистые interface, без Zod)
// ─────────────────────────────────────────────

export interface OrderRow {
  id: string
  requestId: string
  requestNumber: string | null
  clientId: string
  clientName: string
  managerId: string
  managerName: string | null
  status: OrderStatus
  paymentTermsType: OrderPaymentTerms
  prepaymentPercent: number
  deferralDays: number | null
  // Сумма по КП (Σ orderedPrice×qty по ORDERED-позициям) — СПРАВОЧНАЯ.
  totalSum: number
  // Сумма по выставленным счетам (Σ активных OrderInvoice.totalSum) — ИСТОЧНИК
  // ИСТИНЫ по деньгам. Может отличаться от totalSum после ручной правки цен
  // позиций счёта (правки в УНФ). От неё считаются остаток и статус оплаты.
  invoicedSum: number
  // Остаток к оплате = max(0, invoicedSum - paidSum).
  remainingSum: number
  paidSum: number
  invoiceCount: number
  createdAt: string
}

// Кандидат в «сопроводы» (закупщик/менеджер) для назначения по позиции.
export interface HandlerCandidateDto {
  id: string
  name: string | null
  role: string
}

// Позиция заказа: позиция заявки, ушедшая в «Счёт», с выбранной квотой (поставщик,
// себестоимость, цена клиенту) и «сопроводом» (кто закупает). Задел под Блок 3.
export interface OrderPositionDto {
  requestItemId: string
  // Номер позиции = её ранг в заявке по порядку `position` (как «№» в КП и в
  // таблице заявки) — чтобы № в КП и в заказе совпадали.
  position: number
  partNumber: string
  brand: string | null
  /** Сегмент партномера («Наименование» позиции заявки): Диод/Микросхема/Разъём… */
  category: string | null
  quantity: number
  unit: string | null
  matrix: string | null
  status: string
  // Выбранная для КП квота поставщика (orderedQuote).
  supplierName: string | null
  // Заметка к выигравшей квоте (orderedQuote.note).
  note: string | null
  costPrice: number | null
  costCurrency: string | null
  // Цена клиенту (₽, из КП) и сумма.
  customerPrice: number
  sum: number
  // Закупщик, давший выигравшую квоту (для матриц Китай/Производители).
  buyerId: string | null
  buyerName: string | null
  // «Сопровод» — кто закупает позицию: procurement (закупщик-автор квоты) для
  // ВЭД-матриц (Китай/Производители), manager (менеджер заявки) для Склад/РФ/Стоки.
  // Назначение сопровода вручную — Блок 3 (TODO), здесь значение по умолчанию.
  handlerKind: 'procurement' | 'manager'
  // Назначенный сопровод (override) или авто-значение по матрице.
  handlerId: string | null
  /** Позиции нет ни в одном счёте, загруженном из 1С, — «ждёт счёта» (21.10.2026). */
  awaitingInvoice?: boolean
  /** Номер счёта 1С, в котором стоит позиция; её «№» — номер строки в этом счёте. */
  invoiceNumber?: string | null
  handlerName: string | null
}

export interface OrderInvoiceItemDto {
  id: string
  requestItemId: string | null
  partNumber: string
  customerPartNumber: string | null
  brand: string | null
  quantity: number
  unit: string | null
  price: number
  sum: number
  position: number
  /** Ручная правка «Наименования» для УНФ (модалка счёта); null — авто. */
  unfName: string | null
  /** Итоговое «Наименование» для шаблона УНФ: unfName → справочник 1С → категория+парт. */
  unfNameResolved: string
  /** Сегмент партномера («Наименование» позиции заявки): Диод/Микросхема/Разъём… */
  category: string | null
}

export interface OrderInvoiceDto {
  id: string
  orderId: string
  invoiceNumber: string | null
  quotationSnapshotId: string | null
  status: OrderInvoiceStatus
  totalSum: number
  vatRate: number
  /** Режим НДС счёта — из КП (15.10.2026): «С НДС» или «НДС сверху». */
  vatMode: VatMode
  entityId: string | null
  issuedAt: string | null
  // Источник суммы счёта: 'kp' (из КП) / 'manual' (правка цен вручную) / 'unf'
  // (будущий синк из 1С). null у старых счетов до перестройки. Для UI-бейджа.
  amountSource: string | null
  amountEditedAt: string | null
  createdAt: string
  items: OrderInvoiceItemDto[]
}

export interface OrderPaymentDto {
  id: string
  orderId: string
  invoiceId: string | null
  amount: number
  currency: string
  status: OrderPaymentStatus
  kind: string | null
  paidAt: string | null
  note: string | null
  createdAt: string
  /** Откуда оплата: внесена руками или создана разносом банковской выписки. */
  source: OrderPaymentSource
  /** Оплата подтверждена строкой банковской выписки (у «из банка» — всегда). */
  bankConfirmed: boolean
}

/** Код ошибки: такую оплату уже принёс банк — нужно подтверждение, чтобы внести ещё одну. */
export const BANK_PAYMENT_DUPLICATE_CODE = 'BANK_PAYMENT_DUPLICATE'

export interface OrderDocumentDto {
  id: string
  orderId: string
  invoiceId: string | null
  kind: OrderDocumentKind
  fileName: string
  originalName: string | null
  mimeType: string | null
  sizeBytes: number | null
  url: string
  createdAt: string
}

export interface OrderStatusHistoryDto {
  id: string
  fromStatus: OrderStatus | null
  toStatus: OrderStatus
  actorId: string | null
  actorName: string | null
  note: string | null
  createdAt: string
}

export interface OrderDetailDto extends OrderRow {
  notes: string | null
  currency: string
  approvedById: string | null
  approvedByName: string | null
  approvedAt: string | null
  cancelReason: string | null
  /** «Будут ещё счета» из 1С (21.10.2026). */
  moreInvoicesExpected: boolean
  /**
   * Заказ сверен со счётом 1С: все позиции стоят в счетах, загруженных из 1С, и новых
   * счетов не ждём. Только такой заказ пойдёт в раскладку закупки (C8).
   */
  invoiceReconciled: boolean
  positions: OrderPositionDto[]
  invoices: OrderInvoiceDto[]
  payments: OrderPaymentDto[]
  documents: OrderDocumentDto[]
  statusHistory: OrderStatusHistoryDto[]
}

export interface OrderListResponse {
  items: OrderRow[]
  nextCursor: string | null
}

// Строка шаблона счёта — порядок полей соответствует ORDER_INVOICE_COLUMNS.
export interface InvoiceTemplateRow {
  rowNo: number
  // «Наименование» и «Артикул» для УНФ (из справочника номенклатуры 1С 1-в-1,
  // либо fallback category+partNumber / partNumber — см. resolveUnfNaming).
  name: string
  article: string
  partNumber: string
  customerPartNumber: string | null
  brand: string | null
  quantity: number
  unit: string | null
  price: number
  sum: number
  // УНФ-классификация (с 13.07.2026): «Категория» — точная строка УНФ с кодом
  // (fallback «Прочие»), «Входит в группу» — ЭКБ ИП/ОП по алфавиту партномера.
  unfCategory: string
  unfGroup: string
}

// Полезная нагрузка для фронт-генерации Excel-шаблона счёта. Реквизиты продавца
// (ИНН/КПП/адрес/лого) фронт берёт по entityId из kpEntities.ts — бэк отдаёт
// только id юрлица. Форма подтверждена практикой (02.07.2026) — грузится в УНФ.
export interface InvoiceTemplatePayload {
  orderId: string
  /** null — сводный шаблон по всем активным счетам заказа (один документ для УНФ). */
  invoiceId: string | null
  requestNumber: string | null
  clientName: string
  entityId: string | null
  vatRate: number
  /** Режим НДС счёта (15.10.2026): цена в шаблоне всегда с НДС; у «НДС сверху» в конце столбец «НДС». */
  vatMode: VatMode
  totalSum: number
  columns: OrderInvoiceColumn[]
  rows: InvoiceTemplateRow[]
}
